import { useEffect, useState } from "react";
import { api, type AssignmentInfo, type Flag, type Me, type ProjectSummary, dayLabel, lessonLabel } from "./api";
import { Alert } from "./icons";
import { ErrorScreen, Lion, LoadingScreen } from "./Lion";
import { FeaturesContext, FeaturesEntry, HelperLimitsEntry } from "./features";
import { LaunchKitEntry } from "./LaunchKit";
import { LessonPlanButton } from "./LessonPlan";
import { Gallery } from "./Gallery";
import { ShareReview } from "./ShareReview";
import { SchoolCalendarEntry } from "./SchoolCalendar";
import { ClassroomKitEntry } from "./ClassroomKit";
import { ClassDataEntry } from "./ClassData";
import { ClassesEntry, DemoBadge } from "./Classes";
import { PeopleEntry } from "./People";
import { MeContext, RefreshCoinsContext } from "./me";
import { ClassBadge, ClassRules, JoinClass } from "./StudentStart";
import { Submissions, SubmissionsEntry } from "./Submissions";
import { Workspace } from "./Workspace";
import { LiveProject } from "./LiveProject";
import { LiveView, LiveViewEntry } from "./LiveView";
import { PauseOverlay } from "./PauseOverlay";
import { NameMenu } from "./Profile";
import { PromptGolfEntry } from "./PromptGolf";
import { ArenaTeacherEntry, BotArenaEntry } from "./BotArena";
import { ArenaPage } from "./ArenaPage";

// Projector mode: the teacher picks or starts a project, then works in the workspace.

function readProjectFromHash(): string | null {
  const m = /^#project-([0-9a-f-]{36})$/.exec(window.location.hash);
  return m ? m[1]! : null;
}

/** The live view's hash: #live (first class) or #live-<class id>. */
function readLiveFromHash(): { classId: string | null } | null {
  const m = /^#live(?:-([0-9a-f-]{36}))?$/.exec(window.location.hash);
  return m ? { classId: m[1] ?? null } : null;
}

/** A lesson link from Google Classroom: /lesson/<assignment id> opens that lesson (the newest project
 *  for it, or a new one) once the person is signed in and, for a student, has joined their class. */
function readLessonFromPath(): string | null {
  return /^\/lesson\/([a-z0-9-]+)\/?$/.exec(window.location.pathname)?.[1] ?? null;
}

function OpenLesson({ assignmentId, onOpen, onFail }: { assignmentId: string; onOpen: (projectId: string) => void; onFail: () => void }) {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // A lesson that hasn't opened yet goes Home, where its card shows the 🔒 and the day (decision 96).
      const { assignments } = await api.assignments();
      if (assignments.find((a) => a.id === assignmentId)?.locked) throw new Error("locked");
      const { projects } = await api.projects(); // newest first
      const mine = projects.find((p) => p.assignmentId === assignmentId);
      const id = mine?.id ?? (await api.createProject(assignmentId)).id;
      if (!cancelled) onOpen(id);
    })().catch(() => !cancelled && onFail());
    return () => {
      cancelled = true;
    };
  }, [assignmentId]);
  return <LoadingScreen text="Opening your lesson…" />;
}

/** A student's project, read-only, for the teacher (from a flag): #watch-<project id>. */
function readWatchFromHash(): string | null {
  const m = /^#watch-([0-9a-f-]{36})$/.exec(window.location.hash);
  return m ? m[1]! : null;
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(readProjectFromHash());
  const [grading, setGrading] = useState(window.location.hash === "#submissions"); // the teacher's Submissions page
  const [arena, setArena] = useState(window.location.hash === "#arena"); // the teacher's Bot Arena page (decision 99)
  const [live, setLive] = useState(readLiveFromHash()); // the teacher's live view
  const [watch, setWatch] = useState(readWatchFromHash());
  const [lessonLink, setLessonLink] = useState(readLessonFromPath());
  const [large, setLarge] = useState(() => {
    try {
      return localStorage.getItem("buildlab-large") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    api.me().then(setMe, (e: Error) => setError(e.message));
    const onHash = () => {
      setProjectId(readProjectFromHash());
      setGrading(window.location.hash === "#submissions");
      setArena(window.location.hash === "#arena");
      setLive(readLiveFromHash());
      setWatch(readWatchFromHash());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("large", large);
    try {
      localStorage.setItem("buildlab-large", large ? "1" : "0");
    } catch {}
  }, [large]);

  const go = (id: string | null) => {
    window.location.hash = id ? `project-${id}` : "";
    setProjectId(id);
  };

  if (error) return <ErrorScreen message={error} />;
  if (!me) return <LoadingScreen text="Loading BuildLab…" />;
  // A student's first visit: the class code, then the class rules (decision 68).
  const reloadMe = () => api.me().then(setMe, (e: Error) => setError(e.message));
  if (me.user.role === "student" && !me.class) return <JoinClass name={me.user.name} guest={!!me.user.guest} onJoined={reloadMe} />;
  if (me.user.role === "student" && !me.rulesAccepted) return <ClassRules className={me.class!.name} onAccepted={reloadMe} />;
  const setFeatures = (features: Record<string, boolean>) => setMe({ ...me, features });
  const refreshCoins = () => api.me().then((m) => setMe((cur) => (cur ? { ...cur, coins: m.coins } : cur)), () => {});
  return (
    <MeContext.Provider value={me}>
      <RefreshCoinsContext.Provider value={refreshCoins}>
        <FeaturesContext.Provider value={me.features ?? {}}>
          {lessonLink && !projectId ? (
            <OpenLesson
              assignmentId={lessonLink}
              onOpen={(id) => {
                window.history.replaceState(null, "", `/#project-${id}`);
                setLessonLink(null);
                setProjectId(id);
              }}
              onFail={() => {
                window.history.replaceState(null, "", "/");
                setLessonLink(null);
              }}
            />
          ) : projectId ? (
            <Workspace key={projectId} projectId={projectId} onExit={() => go(null)} large={large} onLarge={setLarge} />
          ) : watch && me.user.role === "teacher" ? (
            <LiveProject projectId={watch} onBack={() => go(null)} backLabel="Home" />
          ) : live && me.user.role === "teacher" ? (
            <LiveView classId={live.classId} onExit={() => go(null)} onClass={(id) => window.location.replace(`#live-${id}`)} />
          ) : grading && me.user.role === "teacher" ? (
            <Submissions onExit={() => go(null)} onOpenProject={go} />
          ) : arena && me.user.role === "teacher" ? (
            <ArenaPage onExit={() => go(null)} />
          ) : (
            <Home me={me} onOpen={go} onSubmissions={() => (window.location.hash = "submissions")} onLive={(id) => (window.location.hash = `live-${id}`)} onFeatures={setFeatures} />
          )}
          {me.user.role === "student" && <PauseOverlay />}
        </FeaturesContext.Provider>
      </RefreshCoinsContext.Provider>
    </MeContext.Provider>
  );
}

const REASONS: Record<string, string> = {
  real_person: "About a real person",
  personal_info: "Personal information",
  unsafe: "Unsafe",
  inappropriate: "Not school-appropriate",
  wellbeing: "Student may need help",
  screened_message: "Message to the helper: screening wasn't sure",
  class_share: "Shared with the class: waiting for you",
  class_share_blocked: "Blocked from sharing",
  other: "Other",
};

const when = (t: number) => {
  const d = new Date(t);
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return today ? `Today, ${time}` : `${d.toLocaleDateString([], { weekday: "short" })}, ${time}`;
};

function Home({
  me,
  onOpen,
  onSubmissions,
  onLive,
  onFeatures,
}: {
  me: Me;
  onOpen: (id: string) => void;
  onSubmissions: () => void;
  onLive: (classId: string) => void;
  onFeatures: (f: Record<string, boolean>) => void;
}) {
  const [assignments, setAssignments] = useState<AssignmentInfo[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.assignments().then((r) => setAssignments(r.assignments));
    void api.projects().then((r) => setProjects(r.projects));
    if (me.user.role === "teacher") void api.flags().then((r) => setFlags(r.flags), () => {});
  }, []);

  const lessonOf = (p: ProjectSummary) => assignments.find((a) => a.id === p.assignmentId)?.lesson;
  // A project in a lesson that hasn't opened yet stays shut until its class day (decision 96).
  const lockOf = (p: ProjectSummary) => assignments.find((a) => a.id === p.assignmentId && a.locked);
  // Keep going: the latest project for each lesson, newest first, three at a time (projects come
  // newest first); "Show all" lists every project.
  const [showAllProjects, setShowAllProjects] = useState(false);
  const latestPerLesson = projects.filter((p, i) => !p.assignmentId || projects.findIndex((q) => q.assignmentId === p.assignmentId) === i);
  const shownProjects = showAllProjects ? projects : latestPerLesson.slice(0, 3);

  return (
    <main className="home">
      <header className="hero">
        <div className="hero-in">
          <div className="hero-top">
            <span className="eyebrow gold">Christian Heritage Academy · 8th Grade STEM</span>
            <div className="who">
              <NameMenu me={me} />
              {me.user.guest && <span className="badge-demo guest">Guest</span>}
              {me.class && <ClassBadge name={me.class.name} />}
            </div>
          </div>
          <h1>Build with an agent</h1>
          <p className="lede">Tell the helper what you want, watch how it works, and check what it built. You're the one in charge.</p>
          <p className="model-note">
            {me.model === "demo" ? "Demo mode: no AI is connected yet. The helper makes small, scripted changes." : me.model ? `Helper model: ${me.model}.` : "No AI model is connected."}
          </p>
          <Lion pose="wave" size={182} className="hero-lion" bob label="Roary the lion, waving hello" />
        </div>
      </header>

      <div className="home-body">
        {flags.length > 0 && (
          <section aria-label="Flagged for you" className="flags">
            <h2>
              <Alert size={20} stroke="#B5452F" />
              Flagged for you
            </h2>
            <p className="muted small">The helper declined these, screening wasn't sure about a message to the helper, or screening held back something a student shared with the class, so you can follow up.</p>
            <ul>
              {flags.map((f) => (
                <li key={f.id}>
                  <div className="fmeta">
                    <b>{f.userName}</b>
                    {f.demo && <DemoBadge />} · {f.projectTitle} · {when(f.createdAt)} · <span className="freason">{REASONS[f.reason] ?? f.reason}</span>
                  </div>
                  <blockquote>{f.message}</blockquote>
                  {f.note && <p className="small">{f.shareKind ? "Screening" : "Helper's note"}: {f.note}</p>}
                  {f.shareKind === "gallery" || f.shareKind === "class_data" ? (
                    <ShareReview flag={f} onDone={() => setFlags((all) => all.filter((x) => x.id !== f.id))} />
                  ) : (
                    <div className="row">
                      <button type="button" className="outline small" onClick={() => (window.location.hash = `watch-${f.projectId}`)}>
                        Open project
                      </button>
                      <button
                        type="button"
                        className="linklike"
                        onClick={async () => {
                          await api.reviewFlag(f.id);
                          setFlags((all) => all.filter((x) => x.id !== f.id));
                        }}
                      >
                        Mark reviewed
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {me.user.role === "teacher" && (
          <div className="teacher-tools" aria-label="Teacher tools">
            <ClassesEntry />
            <SubmissionsEntry onOpen={onSubmissions} />
            <LiveViewEntry onOpen={onLive} />
            <ArenaTeacherEntry />
            <SchoolCalendarEntry />
            <ClassroomKitEntry />
            <LaunchKitEntry />
            <ClassDataEntry />
            <HelperLimitsEntry />
            <FeaturesEntry onChange={onFeatures} />
            {me.user.owner && <PeopleEntry list="coteachers" />}
            {me.user.owner && <PeopleEntry list="guests" />}
          </div>
        )}

        <section aria-label="Assignments" className="cards">
          {assignments.map((a) => (
            <div key={a.id} className="card-slot">
              <button
                type="button"
                className={a.locked ? "card locked" : "card"}
                disabled={busy || a.locked}
                title={a.locked && a.opensOn ? `Opens ${dayLabel(a.opensOn)}` : undefined}
                onClick={async () => {
                  setBusy(true);
                  const { id } = await api.createProject(a.id);
                  onOpen(id);
                }}
              >
                <span className="eyebrow">{lessonLabel(a.lesson)}</span>
                <span className="ctitle">{a.title}</span>
                <span className="cdesc">{a.blurb}</span>
                <span className="practice">You'll practice: {a.practice}</span>
                {a.locked && a.opensOn ? (
                  <span className="start locked-start">
                    <span aria-hidden="true">🔒</span> Opens {dayLabel(a.opensOn)}
                  </span>
                ) : (
                  <span className="start">Start</span>
                )}
              </button>
              {/* Teachers only: the lesson's goals, flow and what to watch for (LessonPlan.tsx). */}
              {me.user.role === "teacher" && <LessonPlanButton assignmentId={a.id} lesson={a.lesson} />}
            </div>
          ))}
        </section>

        <PromptGolfEntry />
        <BotArenaEntry onOpen={onOpen} />
        <Gallery teacher={me.user.role === "teacher"} onOpen={onOpen} />

        {projects.length === 0 && (
          <section aria-label="Keep going" className="keep">
            <h2>Keep going</h2>
            <div className="empty-lion">
              <Lion pose="think" size={78} label="Roary, thinking" />
              <p>
                <b>No projects yet</b>
                Pick a lesson above to start your first one. Your projects will wait for you here.
              </p>
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section aria-label="Keep going" className="keep">
            <h2>Keep going</h2>
            <ul className="projects">
              {shownProjects.map((p) => {
                const lock = lockOf(p);
                return (
                  <li key={p.id}>
                    <button type="button" disabled={!!lock} className={lock ? "locked" : undefined} onClick={() => onOpen(p.id)}>
                      <span className="pthumb" aria-hidden="true">
                        {lessonOf(p) === 0 ? "D" : (lessonOf(p) ?? "·")}
                      </span>
                      <span className="pname">{p.title}</span>
                      <span className="pwhen">{lock?.opensOn ? `🔒 Opens ${dayLabel(lock.opensOn)}` : when(p.updatedAt)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {projects.length > shownProjects.length || showAllProjects ? (
              <button type="button" className="linklike keep-more" onClick={() => setShowAllProjects(!showAllProjects)}>
                {showAllProjects ? "Show fewer" : `Show all ${projects.length} projects`}
              </button>
            ) : null}
          </section>
        )}

        <p className="verse">"Whatever you do, work at it with all your heart." · Colossians 3:23</p>
      </div>
    </main>
  );
}
